import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import Seo from "@/components/Seo";
import { CalendarCheck, CheckCircle, X, Warning } from "@phosphor-icons/react";

export default function RescheduleAccept() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/reschedule/${token}`)
      .then((r) => setProposal(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer valid."));
  }, [token]);

  const accept = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      await api.post(`/reschedule/${token}/accept`, { chosen_starts_at: chosen });
      setDone("accepted");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save your choice — please try again.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!window.confirm("None of these times work? We'll get someone to call you to find another time.")) return;
    setBusy(true);
    try {
      await api.post(`/reschedule/${token}/decline`);
      setDone("declined");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save — please try again.");
    } finally { setBusy(false); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: "Australia/Brisbane" });

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="reschedule-page">
      <Seo title="Pick a new session time — Puppl" description="" />
      <PublicHeader />

      <div className="max-w-2xl mx-auto px-6 py-16">
        {error && !proposal && (
          <div className="card-pp p-10 text-center">
            <Warning size={40} weight="duotone" className="text-amber-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">Link unavailable</h1>
            <p className="text-stone-600">{error}</p>
          </div>
        )}

        {!error && !proposal && <div className="text-center text-stone-500">Loading…</div>}

        {proposal && done === "accepted" && (
          <div className="card-pp p-10 text-center" data-testid="reschedule-done">
            <CheckCircle size={48} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">All set!</h1>
            <p className="text-stone-700">{proposal.dog_name}'s session has been moved to <strong>{fmt(chosen)}</strong>. We've let the team know.</p>
          </div>
        )}

        {proposal && done === "declined" && (
          <div className="card-pp p-10 text-center" data-testid="reschedule-declined">
            <CheckCircle size={48} weight="duotone" className="text-blue-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-stone-900 mb-2">No worries</h1>
            <p className="text-stone-700">We'll be in touch shortly to find a time that works.</p>
          </div>
        )}

        {proposal && !done && (
          <div className="card-pp p-8" data-testid="reschedule-card">
            <p className="text-xs uppercase tracking-wider font-semibold text-[#f38838] mb-2">Reschedule request</p>
            <h1 className="font-heading text-3xl font-bold text-stone-900 mb-2">
              Pick a new time for {proposal.dog_name || "your session"}
            </h1>
            <p className="text-stone-600 mb-5">
              Hi {(proposal.client_name || "").split(" ")[0]} — the team needs to move your session originally booked for
              {" "}<strong>{fmt(proposal.original_starts_at)}</strong>.
            </p>
            {proposal.message && (
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 text-sm italic text-stone-700 mb-5">
                "{proposal.message}"
              </div>
            )}
            <div className="space-y-2 mb-5">
              {(proposal.suggestions || []).map((s, i) => (
                <label key={s} className={`block rounded-xl border-2 p-4 cursor-pointer transition-all ${chosen === s ? "border-[#f38838] bg-orange-50" : "border-stone-200 hover:border-stone-300"}`} data-testid={`slot-${i}`}>
                  <input type="radio" name="slot" value={s} checked={chosen === s} onChange={() => setChosen(s)} className="mr-3 accent-[#f38838]" />
                  <span className="font-semibold text-stone-900">Option {i + 1}</span>
                  <span className="block text-sm text-stone-600 ml-7">{fmt(s)}</span>
                </label>
              ))}
            </div>
            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
            <button
              onClick={accept}
              disabled={!chosen || busy}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="reschedule-accept-btn"
            >
              <CalendarCheck size={16} weight="bold" /> {busy ? "Saving…" : "Confirm this time"}
            </button>
            <button
              onClick={decline}
              disabled={busy}
              className="w-full mt-3 text-sm text-stone-500 hover:text-stone-900 inline-flex items-center justify-center gap-1"
              data-testid="reschedule-decline-btn"
            >
              <X size={12} /> None of these work — ask me to call
            </button>
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
}
